import { Link } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import BottomSheet, { useBottomSheet } from "@/components/ui/bottomSheet";
import { useBreakpointContext } from "@/contexts/BreakpointContext";
import type { ProductCategory } from "@/types/productCategory";

interface ProductCategoriesProps {
  categories: ProductCategory[];
  currentCategory: ProductCategory | null;
  isLoading: boolean;
  checkIsActiveCategory: (categorySlug?: string) => boolean;
}

const ProductCategories = ({
  categories,
  currentCategory,
  isLoading,
  checkIsActiveCategory,
}: ProductCategoriesProps) => {
  const { isMobile, isTablet, isDesktop } = useBreakpointContext();
  const categoriesSheet = useBottomSheet();

  if (isLoading && isDesktop) {
    return <CategoriesDesktopSkeleton />;
  }

  if (categories.length === 0) return null;

  if (isMobile || isTablet) {
    return (
      <div className="relative">
        <button
          type="button"
          className="group btn btn-secondary btn-md flex w-full justify-between font-normal"
          onClick={categoriesSheet.handleOpen}
        >
          <div>
            <span className="text-neutral">分類 </span>
            {currentCategory ? currentCategory.name : "全部商品"}
          </div>
          <ChevronDownIcon className="size-4" />
        </button>
        <BottomSheet
          show={categoriesSheet.isOpen}
          onClose={categoriesSheet.handleClose}
          title="商品分類"
        >
          <CategoriesMenu
            categories={categories}
            checkIsActiveCategory={checkIsActiveCategory}
            onClose={categoriesSheet.handleClose}
          />
        </BottomSheet>
      </div>
    );
  }

  return (
    <>
      <h2 className="brand-text-lg mb-4 font-bold text-base-content">商品分類</h2>
      <CategoriesMenu
        categories={categories}
        checkIsActiveCategory={checkIsActiveCategory}
        onClose={() => {}}
      />
    </>
  );
};

const CategoriesMenu = ({
  categories,
  checkIsActiveCategory,
  onClose,
}: {
  categories: ProductCategory[];
  checkIsActiveCategory: (categorySlug?: string) => boolean;
  onClose: () => void;
}) => {
  return (
    <>
      <ul className="space-y-4 lg:space-y-2">
        <AllProductsLink checkIsActiveCategory={checkIsActiveCategory} onClose={onClose} />
        {categories.map((category) => (
          <CategoryLink
            key={category.id}
            category={category}
            checkIsActiveCategory={checkIsActiveCategory}
            onClose={onClose}
          />
        ))}
      </ul>
    </>
  );
};

const AllProductsLink = ({
  checkIsActiveCategory,
  onClose,
}: {
  checkIsActiveCategory: (categorySlug?: string) => boolean;
  onClose: () => void;
}) => (
  <Link to="/shop" onClick={onClose}>
    <div className="group mb-3 flex justify-between">
      <span
        className={clsx(
          "brand-text-md font-normal",
          checkIsActiveCategory() ? "font-bold text-secondary" : "",
        )}
      >
        全部商品
      </span>
    </div>
  </Link>
);

const CategoryLink = ({
  category,
  parentCategory = null,
  checkIsActiveCategory,
  onClose,
}: {
  category: ProductCategory;
  parentCategory?: ProductCategory | null;
  checkIsActiveCategory: (categorySlug?: string) => boolean;
  onClose: () => void;
}) => {
  if (!category) return null;

  const isSubCategory = !!parentCategory;
  const linkTo = isSubCategory
    ? `/product-category/${parentCategory.slug}/${category.slug}`
    : `/product-category/${category.slug}`;

  return (
    <li className="space-y-4 lg:space-y-2">
      <Link to={linkTo} onClick={onClose}>
        <div
          className={clsx("group flex items-center justify-between", isSubCategory ? "pl-4" : "")}
        >
          <span
            className={clsx(
              "brand-text-md group-hover:brand-link font-normal",
              checkIsActiveCategory(category.slug) ? "font-bold text-primary" : "",
            )}
          >
            {category.name}
          </span>
          <span
            className={clsx(
              "badge badge-ghost group-hover:badge-accent group-hover:opacity-50",
              checkIsActiveCategory(category.slug) ? "!badge-accent opacity-50" : "",
            )}
          >
            {category.count}
          </span>
        </div>
      </Link>
      {category.children?.length > 0 && (
        <ul className="space-y-4 lg:space-y-2">
          {category.children.map((subCategory) => (
            <CategoryLink
              key={subCategory.id}
              category={subCategory}
              parentCategory={category}
              checkIsActiveCategory={checkIsActiveCategory}
              onClose={onClose}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const CategoriesDesktopSkeleton = () => (
  <ul className="space-y-4 lg:space-y-2">
    {Array.from({ length: 16 }).map((_, index) => (
      <li
        key={index}
        className={clsx(
          "h-5 animate-pulse bg-base-300",
          index % 3 === 1 || index % 3 === 2 ? "ml-4 w-1/2" : "w-3/4",
        )}
      ></li>
    ))}
  </ul>
);

export default ProductCategories;
